import Papa from 'papaparse';
import { Asignatura, Evaluacion, Material } from '../logic/types';

export function exportToCSV(data: any[], filename: string) {
  const csv = Papa.unparse(data);
  // Agregar BOM UTF-8 (\uFEFF / EF BB BF) al inicio para compatibilidad con Excel en Windows
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Genera y descarga el archivo CSV/Excel de Asignaturas respetando exactamente
 * las mismas columnas de la plantilla de importación de malla curricular:
 * "Nombre", "Código", "Semestre", "Créditos", "Departamento", "Facultad de origen", "Previas"
 */
export function exportMallaFormatCSV(asignaturas: Asignatura[]) {
  const codeMap = new Map<string, string>();
  asignaturas.forEach(a => {
    if (a.codigo) codeMap.set(a.id, a.codigo);
    else codeMap.set(a.id, a.nombre);
  });

  const rows = asignaturas
    .filter(a => !a.en_papelera)
    .map(a => {
      // Reconstruir previas como códigos o nombres separados por coma
      const previasList: string[] = [];
      if (a.requisitos) {
        a.requisitos.forEach(reqGroup => {
          reqGroup.forEach(req => {
            if (req.tipo === 'materia_aprobada' || req.tipo === 'curso_aprobado') {
              const codeOrName = codeMap.get(req.id);
              if (codeOrName && !previasList.includes(codeOrName)) {
                previasList.push(codeOrName);
              }
            }
          });
        });
      }

      return {
        Nombre: a.nombre,
        Código: a.codigo || '',
        Semestre: a.semestre_sugerido || '',
        Créditos: a.creditos || 0,
        Departamento: a.departamento || '',
        'Facultad de origen': a.facultad_origen || 'FCEA',
        Previas: previasList.join(','),
      };
    });

  exportToCSV(rows, `malla_curricular_fcea_${new Date().toISOString().split('T')[0]}.csv`);
}

/**
 * Exporta las Evaluaciones en formato CSV
 */
export function exportEvaluacionesCSV(evaluaciones: Evaluacion[], asignaturas: Asignatura[]) {
  const asigMap = new Map(asignaturas.map(a => [a.id, a.nombre]));
  const rows = evaluaciones.map(e => ({
    Asignatura: asigMap.get(e.asignatura_id) || e.asignatura_id,
    Tipo: e.tipo,
    Fecha: e.fecha || '',
    Nota: e.nota ?? '',
    'Escala Calificación': e.escala || '',
    'Porcentaje Logro (%)': e.porcentaje_logro ? e.porcentaje_logro.toFixed(1) : '',
    Concepto: e.concepto || '',
  }));

  exportToCSV(rows, `evaluaciones_fcea_${new Date().toISOString().split('T')[0]}.csv`);
}

/**
 * Exporta los Materiales en formato CSV
 */
export function exportMaterialesCSV(materiales: Material[], asignaturas: Asignatura[]) {
  const asigMap = new Map(asignaturas.map(a => [a.id, a.nombre]));
  const rows = materiales.map(m => ({
    Asignatura: asigMap.get(m.asignatura_id) || m.asignatura_id,
    Nombre: m.nombre,
    Tipo: m.tipo,
    Ruta: m.ruta_relativa,
    Tags: m.tags || '',
    'Fecha Agregado': m.fecha_agregado,
  }));

  exportToCSV(rows, `materiales_fcea_${new Date().toISOString().split('T')[0]}.csv`);
}

/**
 * Exporta una escolaridad / historial académico en formato de documento impreso PDF (vía window.print con layout prolijo)
 */
export function exportHistorialImpresoPDF(asignaturas: Asignatura[], promedio: number, creditosTotales: number) {
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    alert('Por favor permite las ventanas emergentes para generar el documento PDF.');
    return;
  }

  const activas = asignaturas.filter(a => !a.en_papelera);
  const aprobadas = activas.filter(a => a.estado === 'aprobada');
  const enCurso = activas.filter(a => a.estado === 'en_curso' || a.estado === 'a_examen');
  const pendientes = activas.filter(a => a.estado === 'no_iniciada' || a.estado === 'no_cursada');

  const fechaHoy = new Date().toLocaleDateString('es-UY', { day: '2-digit', month: 'long', year: 'numeric' });

  const html = `
<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8" />
  <title>Escolaridad e Historial Académico — Malla</title>
  <style>
    @media print {
      @page { margin: 15mm; size: A4; }
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
    body {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
      color: #0f172a;
      background: #ffffff;
      margin: 0;
      padding: 24px;
      font-size: 12px;
      line-height: 1.4;
    }
    .header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      border-b: 2px solid #EA514D;
      padding-bottom: 16px;
      margin-bottom: 20px;
    }
    .brand-title {
      font-size: 20px;
      font-weight: 800;
      color: #EA514D;
      margin: 0;
      letter-spacing: -0.5px;
    }
    .brand-sub {
      font-size: 11px;
      color: #64748b;
      margin-top: 2px;
      font-weight: 500;
    }
    .meta-box {
      text-align: right;
      font-size: 11px;
      color: #475569;
    }
    .summary-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 12px;
      margin-bottom: 24px;
      background: #f8fafc;
      padding: 14px;
      border-radius: 8px;
      border: 1px solid #e2e8f0;
    }
    .stat-label {
      font-size: 10px;
      text-transform: uppercase;
      font-weight: 700;
      color: #64748b;
    }
    .stat-val {
      font-size: 18px;
      font-weight: 800;
      color: #EA514D;
      margin-top: 2px;
    }
    .section-title {
      font-size: 13px;
      font-weight: 700;
      color: #0f172a;
      border-left: 3px solid #EA514D;
      padding-left: 8px;
      margin-top: 20px;
      margin-bottom: 10px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 16px;
      font-size: 11px;
    }
    th {
      background: #f1f5f9;
      color: #334155;
      text-align: left;
      padding: 8px 10px;
      font-weight: 700;
      border-bottom: 1px solid #cbd5e1;
      font-size: 10px;
      text-transform: uppercase;
    }
    td {
      padding: 8px 10px;
      border-bottom: 1px solid #e2e8f0;
    }
    .badge {
      display: inline-block;
      padding: 2px 6px;
      border-radius: 4px;
      font-size: 9px;
      font-weight: 700;
      text-transform: uppercase;
    }
    .badge-approved { background: #dcfce7; color: #15803d; }
    .badge-in-course { background: #dbeafe; color: #1e40af; }
    .badge-pending { background: #f1f5f9; color: #64748b; }
    .footer {
      margin-top: 40px;
      border-top: 1px solid #e2e8f0;
      padding-top: 12px;
      text-align: center;
      font-size: 10px;
      color: #94a3b8;
    }
  </style>
</head>
<body>
  <div class="header">
    <div>
      <h1 class="brand-title">Escolaridad e Historial Académico</h1>
      <p class="brand-sub">Facultad de Ciencias Económicas y de Administración (FCEA - Udelar)</p>
    </div>
    <div class="meta-box">
      <div><strong>Emisión:</strong> ${fechaHoy}</div>
      <div><strong>Documento:</strong> Reporte de Estudiante</div>
    </div>
  </div>

  <div class="summary-grid">
    <div>
      <div class="stat-label">Créditos Aprobados</div>
      <div class="stat-val">${creditosTotales} CR</div>
    </div>
    <div>
      <div class="stat-label">Promedio de Escolaridad</div>
      <div class="stat-val">${promedio.toFixed(2)} pts</div>
    </div>
    <div>
      <div class="stat-label">Materias Aprobadas</div>
      <div class="stat-val">${aprobadas.length} / ${activas.length}</div>
    </div>
  </div>

  <div class="section-title">Asignaturas Aprobadas</div>
  <table>
    <thead>
      <tr>
        <th>Código</th>
        <th>Asignatura</th>
        <th>Semestre</th>
        <th>Créditos</th>
        <th>Calificación</th>
        <th>Concepto</th>
      </tr>
    </thead>
    <tbody>
      ${aprobadas.length === 0 ? '<tr><td colspan="6" style="text-align:center; color:#94a3b8;">Sin asignaturas aprobadas registradas.</td></tr>' : 
        aprobadas.map(a => `
          <tr>
            <td><strong>${a.codigo || '-'}</strong></td>
            <td>${a.nombre}</td>
            <td>Semestre ${a.semestre_sugerido || '-'}</td>
            <td>${a.creditos} CR</td>
            <td><strong>${a.nota_final !== undefined ? a.nota_final : (a.calificacion_oficial !== null ? a.calificacion_oficial : '-')}</strong></td>
            <td>${a.concepto_final || '-'}</td>
          </tr>
        `).join('')
      }
    </tbody>
  </table>

  <div class="section-title">Asignaturas En Cursado / En Examen</div>
  <table>
    <thead>
      <tr>
        <th>Código</th>
        <th>Asignatura</th>
        <th>Semestre</th>
        <th>Créditos</th>
        <th>Estado</th>
      </tr>
    </thead>
    <tbody>
      ${enCurso.length === 0 ? '<tr><td colspan="5" style="text-align:center; color:#94a3b8;">No hay asignaturas en cursado actualmente.</td></tr>' :
        enCurso.map(a => `
          <tr>
            <td><strong>${a.codigo || '-'}</strong></td>
            <td>${a.nombre}</td>
            <td>Semestre ${a.semestre_sugerido || '-'}</td>
            <td>${a.creditos} CR</td>
            <td><span class="badge badge-in-course">${a.estado === 'a_examen' ? 'A Examen' : 'En Curso'}</span></td>
          </tr>
        `).join('')
      }
    </tbody>
  </table>

  <div class="section-title">Asignaturas Pendientes (${pendientes.length})</div>
  <table>
    <thead>
      <tr>
        <th>Código</th>
        <th>Asignatura</th>
        <th>Semestre</th>
        <th>Créditos</th>
        <th>Departamento</th>
      </tr>
    </thead>
    <tbody>
      ${pendientes.slice(0, 15).map(a => `
        <tr>
          <td>${a.codigo || '-'}</td>
          <td>${a.nombre}</td>
          <td>Semestre ${a.semestre_sugerido || '-'}</td>
          <td>${a.creditos} CR</td>
          <td>${a.departamento || 'General'}</td>
        </tr>
      `).join('')}
      ${pendientes.length > 15 ? `<tr><td colspan="5" style="text-align:center; color:#64748b; font-style:italic;">+ ${pendientes.length - 15} asignaturas pendientes más...</td></tr>` : ''}
    </tbody>
  </table>

  <div class="footer">
    Generado automáticamente por Malla • Documento de seguimiento personal e historial académico.
  </div>

  <script>
    window.onload = function() {
      window.print();
    };
  </script>
</body>
</html>
  `;

  printWindow.document.write(html);
  printWindow.document.close();
}
